import { useEffect, useState } from 'react';
import { apiFetch } from '../lib/api';
import { Ayuda } from '../components/Ayuda';
import { Spinner } from '../components/Spinner';
import ChatAndres from './ChatAndres';

/**
 * Investigador SEIA — Elena (A20, 2026-10-20). El chat ES la página: reusa ChatAndres sobre el
 * favorito global `__investigador_seia__` (GET /api/dia/investigador-seia); el worker detecta ese
 * favorito y responde como Elena. Sin capítulos ni botón "volver" (no hay proyecto detrás): Elena
 * consulta el portal público del SEA, no el corpus de una DIA.
 * Regla 2026-10-19: toda funcionalidad explica CÓMO se usa y POR QUÉ funciona así → ⓘ en el
 * título (campo `ayuda` del NavItem, lo pinta el layout) y chips persistentes bajo él (citas,
 * espera, escaneados, casos de terceros).
 */

/** Notas de uso (cómo y por qué). Persisten bajo el título para que no dependan del estado vacío. */
const NOTAS: { chip: string; texto: string }[] = [
  {
    chip: 'Cita expediente y URL',
    texto:
      'Cada respuesta indica el número de expediente y el enlace al portal del SEA de donde salió el dato. Así cualquier cifra, fecha o resolución se puede verificar en la fuente oficial: trazabilidad, no memoria del modelo.',
  },
  {
    chip: 'Puede tardar',
    texto:
      'Elena consulta el portal en vivo en el momento en que preguntas (no hay copia local del e-SEIA). Para respetar el sitio del SEA hace una petición por segundo, así que un catastro grande o un expediente con muchos documentos tarda más que un chat normal.',
  },
  {
    chip: 'Escaneado, no leído',
    texto:
      'Algunos oficios y resoluciones del portal son imágenes escaneadas sin texto: Elena los ve pero no puede leerlos. Si lo pides expresamente, los lee con OCR (reconocimiento de texto); tiene costo y se hace solo a pedido.',
  },
  {
    chip: 'Casos de terceros',
    texto:
      'Lo que Elena encuentra son proyectos de otros titulares, útiles como referencia y lecciones aprendidas. Nunca se mezclan con los proyectos DIA en curso de AMBEC: para trabajar una DIA propia está Andrés en Agentes → DIA.',
  },
];

const EJEMPLOS = [
  '¿Qué parques fotovoltaicos se aprobaron en Pozo Almonte desde 2024?',
  '¿Qué DIAs fueron rechazadas en la Región de Tarapacá y por qué?',
  '¿Qué le observaron los servicios al expediente 2163609551?',
];

export default function InvestigadorSeia() {
  const [favorito, setFavorito] = useState<{ id: string; nombre: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void apiFetch<{ favorito: { id: string; nombre: string } }>('/api/dia/investigador-seia')
      .then((d) => setFavorito(d.favorito))
      .catch((e) => setError(e instanceof Error ? e.message : 'No se pudo abrir el Investigador SEIA.'));
  }, []);

  return (
    <div className="flex h-[calc(100dvh-165px)] min-h-[440px] flex-col lg:h-[calc(100dvh-130px)]">
      {/* El título "Agente eSEIA — Elena" + ⓘ lo pinta el layout desde navItems (campo `ayuda`).
          Aquí van las notas de uso persistentes, pegadas bajo ese título. */}
      <header className="-mt-3 shrink-0 pb-3">
        <ul className="flex flex-wrap gap-1.5" aria-label="Cómo trabaja Elena">
          {NOTAS.map((n) => (
            <li key={n.chip}>
              <Ayuda texto={n.texto}>
                <button
                  type="button"
                  className="inline-flex h-7 items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 text-[11.5px] font-normal text-texto transition-colors hover:border-celeste hover:bg-celeste/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-azul-profundo"
                >
                  {n.chip}
                  <svg viewBox="0 0 24 24" className="h-[13px] w-[13px] text-gris/70" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="12" cy="12" r="9.5" /><path d="M12 16.5v-5M12 7.75h.01" />
                  </svg>
                </button>
              </Ayuda>
            </li>
          ))}
        </ul>
      </header>

      {error && <p role="alert" className="alerta-error">{error}</p>}
      {!favorito && !error && (
        <div className="flex flex-1 items-center justify-center gap-2 text-sm text-gris"><Spinner tamano="xs" /> Abriendo el Investigador SEIA…</div>
      )}
      {favorito && (
        <ChatAndres
          favoritoId={favorito.id}
          nombre="Portal e-SEIA"
          agente="Elena"
          sinCapitulos
          etiquetaMemoria="Memoria del Investigador"
          claseAltura="min-h-0 flex-1 rounded-xl border border-slate-200/70"
          placeholderVacio="Pregúntale a Elena por proyectos evaluados en el SEIA: catastros por comuna o región, fichas de expediente, qué observaron los servicios. Toca un ejemplo para empezar (se escribe en el campo, lo envías tú)."
          badgesIniciales={EJEMPLOS}
          vacioExtra={
            <p className="max-w-md text-center text-[11.5px] leading-relaxed text-gris">
              Elena responde con expediente y enlace al SEA para que puedas verificar cada dato; consulta el portal en vivo,
              así que una pregunta amplia puede tardar un poco.
            </p>
          }
        />
      )}
    </div>
  );
}
